import { Activity, ChevronDown, Headset, LayoutDashboard, Target, Ticket, Wallet, type LucideIcon } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import { NavLink } from 'react-router-dom'


import { cn } from '@/lib/cn'
import { hasAnyPermission } from '@/lib/permissions'
import { useAuth } from '@/providers/auth-context'

export interface SidebarItem {
  to: string
  label: string
  icon: LucideIcon
  /** Permissões necessárias (any-of). Array vazio = público pros logados. */
  requiresAny: readonly string[]
}

export interface SidebarBloco {
  title: string
  itens: readonly SidebarItem[]
}

export const SIDEBAR_BLOCOS: readonly SidebarBloco[] = [
  {
    title: 'Bloco 1 (financeiro)',
    itens: [
      { to: '/app/financeiro', label: 'Painel', icon: Wallet, requiresAny: ['view:financeiro'] },
    ],
  },
  {
    // Nomes padronizados pela Lay (#f3ad): Pré-vendas > Vendas > Próximos Eventos.
    title: 'Bloco 2 (comercial)',
    itens: [
      {
        to: '/app/comercial/pre-vendas',
        label: 'Pré-vendas',
        icon: Headset,
        requiresAny: ['view:pre-vendas'],
      },
      { to: '/app/comercial', label: 'Vendas', icon: LayoutDashboard, requiresAny: [] },
      { to: '/app/comercial/metas', label: 'Metas', icon: Target, requiresAny: [] },
    ],
  },
  {
    // Os três blocos vivem numa tela só: com 1–4 indicadores cada, telas
    // separadas ficariam vazias.
    title: 'Blocos 3, 4 e 5 (base, operação e cadência)',
    itens: [
      { to: '/app/operacao', label: 'Painel', icon: Activity, requiresAny: ['view:operacao'] },
    ],
  },
  {
    title: 'Eventos',
    itens: [
      {
        to: '/app/eventos/proximos-eventos',
        label: 'Próximos Eventos',
        icon: Ticket,
        requiresAny: ['view:sympla'],
      },
    ],
  },
]

const CHAVE_BLOCOS_FECHADOS = 'sidebar:blocos-fechados'

// Persistência best-effort do estado recolhido: storage indisponível/corrompido
// degrada para "tudo aberto", nunca quebra a navegação.
function lerBlocosFechados(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(CHAVE_BLOCOS_FECHADOS)
    return new Set<string>(raw === null ? [] : (JSON.parse(raw) as string[]))
  } catch {
    return new Set()
  }
}

function salvarBlocosFechados(fechados: ReadonlySet<string>): void {
  try {
    localStorage.setItem(CHAVE_BLOCOS_FECHADOS, JSON.stringify([...fechados]))
  } catch {
    // Storage cheio/aba privada: segue sem persistir.
  }
}

export function SidebarBrand(): ReactElement {
  return (
    <div className="flex items-center gap-2.5 px-6 py-5">
      <img src="/enjoy-logo.png" alt="Enjoy" className="h-8 w-auto" />
      <span className="text-sidebar-foreground/90 text-lg font-semibold tracking-tight">
        Relatórios
      </span>
    </div>
  )
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const { user } = useAuth()
  const userPerms = user?.permissions ?? []
  const [fechados, setFechados] = useState<ReadonlySet<string>>(lerBlocosFechados)

  const alternar = (title: string): void => {
    const proximo = new Set(fechados)
    if (proximo.has(title)) proximo.delete(title)
    else proximo.add(title)
    salvarBlocosFechados(proximo)
    setFechados(proximo)
  }

  return (
    <nav className="flex-1 space-y-2 overflow-y-auto px-3">
      {SIDEBAR_BLOCOS.map((bloco) => {
        const aberto = !fechados.has(bloco.title)
        return (
          <div key={bloco.title} className="space-y-1">
            <button
              type="button"
              onClick={() => {
                alternar(bloco.title)
              }}
              aria-expanded={aberto}
              // Título longo quebra em linha extra (não trunca) com o chevron no topo.
              className="text-sidebar-foreground/50 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/80 flex min-h-[44px] w-full items-start justify-between gap-2 rounded-md px-3 py-2 text-left text-xs font-semibold uppercase leading-tight tracking-wide transition-colors md:min-h-0"
            >
              <span className="min-w-0 break-words">{bloco.title}</span>
              <ChevronDown
                className={cn(
                  'mt-px h-3.5 w-3.5 shrink-0 transition-transform',
                  aberto ? '' : '-rotate-90',
                )}
                aria-hidden
              />
            </button>
            {aberto ? (
              <div className="space-y-1">
                {bloco.itens
                  .filter((item) => hasAnyPermission(userPerms, item.requiresAny))
                  .map((item) => {
                    const Icon = item.icon
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                              : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                          )
                        }
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        {item.label}
                      </NavLink>
                    )
                  })}
              </div>
            ) : null}
          </div>
        )
      })}
    </nav>
  )
}

export function Sidebar(): ReactElement {
  return (
    <aside className="border-sidebar-border bg-sidebar text-sidebar-foreground sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r md:flex">
      <SidebarBrand />
      <SidebarNav />
      <div className="border-sidebar-border text-sidebar-foreground/60 border-t px-6 py-4 text-xs">
        Relatórios Enjoy
      </div>
    </aside>
  )
}
